import React, { forwardRef, HTMLProps, ReactElement } from "react";
import styled from "styled-components";
import "react-phone-number-input/style.css";
import CustomPhoneInput from "react-phone-number-input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import DPI from "react-day-picker/DayPickerInput";
// the export from this module is incorrect, see https://github.com/gpbl/react-day-picker/issues/981 for more info
// for now, we're using the workround outline here https://github.com/gpbl/react-day-picker/issues/1194#issuecomment-814065458
// eslint-disable-next-line
// @ts-ignore
const DayPickerInput = DPI.__esModule ? DPI.default : DPI;
import { DayPickerInputProps } from "react-day-picker";
import { wrapDayPickerStyles } from "../date_picker";
import "react-day-picker/lib/style.css";
import { ToggleTipProps } from "../../layout/toggle_tip";

interface Props extends HTMLProps<HTMLInputElement> {
  type: "text" | "number" | "password" | "email" | "range";
  label?: string;
  helperText?: string;
  adornmentIcon?: IconProp;
  className?: string;
  error?: string;
  toggleTip?: ReactElement<ToggleTipProps>;
  description?: string;
  questionIsOptional?: boolean;
  labelProps?: ReactElement<ToggleTipProps>;
}

const StyledDescription = styled.span`
  display: block;
  font-weight: 500;
  font-size: 13px;
  margin-top: 7px;
  margin-bottom: 24px;
  color: ${({ theme }) => theme.deprecatedColors.accent.five};
`;

const RequiredMarker = styled.span`
  color: ${({ theme }) => theme.deprecatedColors.error};
`;

const StyledInput = styled.span<{ hasError: boolean }>`
  position: relative;
  display: block;

  .label {
    display: inline-block;
    padding-bottom: ${({ theme }) => theme.sizing.scale100};
    .label-text {
      text-transform: uppercase;
      color: ${({ theme }) => theme.colors.contentPrimary};
      font-size: ${({ theme }) => theme.font.size.label.one};
      padding-right: ${({ theme }) => theme.sizing.scale100};
    }
  }

  .inputWrapper {
    display: inline-block;
    width: 100%;
    position: relative;

    .adornmentIcon {
      color: ${({ theme, hasError }) =>
        hasError ? theme.deprecatedColors.error : theme.deprecatedColors.gray.eight};
      position: absolute;
      top: 16px;
      right: 16px;
    }

    .input:focus + .adornmentIcon {
      color: ${({ theme, hasError }) =>
        hasError ? theme.deprecatedColors.error : theme.deprecatedColors.primary.normal};
    }

    .input:disabled {
      color: ${({ theme }) => theme.deprecatedColors.gray.eight};
    }

    .input:disabled + .adornmentIcon {
      color: ${({ theme }) => theme.deprecatedColors.gray.eight};
    }
  }

  .input {
    color: ${({ theme }) => theme.deprecatedColors.text.normal};
    font-size: ${({ theme }) => theme.font.size.body.one};
    box-sizing: border-box;
    width: 100%;
    height: ${({ theme }) => theme.deprecatedSpacing.component.nine};
    background-color: ${({ theme }) => theme.deprecatedColors.white};
    vertical-align: middle;
    border: 1px solid
      ${({ theme, hasError }) =>
        hasError ? theme.deprecatedColors.error : theme.deprecatedColors.gray.six};
    border-radius: ${({ theme }) => theme.deprecatedSpacing.component.two};
    box-sizing: border-box;
    padding: 8px;
    &.phone {
      padding-left: 60px;
    }

    &:hover {
      border-color: ${({ theme, hasError }) =>
        hasError ? theme.deprecatedColors.error : theme.deprecatedColors.gray.five};
    }

    &:focus {
      border-color: ${({ theme, hasError }) =>
        hasError ? theme.deprecatedColors.error : theme.deprecatedColors.primary.normal};
      outline-style: none;
    }
  }

  .helperText,
  .errorText {
    display: block;
    color: ${({ theme, hasError }) =>
      hasError ? theme.deprecatedColors.error : theme.deprecatedColors.gray.six};
    font-size: ${({ theme }) => theme.font.size.body.three};
    line-height: ${({ theme }) => theme.font.size.body.two};
    margin-left: ${({ theme }) => theme.deprecatedSpacing.component.four};
    margin-top: ${({ theme }) => theme.deprecatedSpacing.component.three};
  }

  .PhoneInputCountry {
    position: absolute;
    left: 16px;
    top: 16px;
  }

  .PhoneInputInput {
    height: 100%;
    border: none;
    color: inherit;
    font-size: inherit;
    vertical-align: middle;
  }

  .DayPickerInput {
    width: 100%;
  }
`;

export const InputComponent = forwardRef<HTMLInputElement, Props>(function Input(
  {
    label,
    helperText,
    adornmentIcon,
    type,
    className,
    error,
    toggleTip,
    description,
    questionIsOptional,
    labelProps,
    id,
    ...inputProps
  }: Props,
  ref,
) {
  return (
    <StyledInput hasError={!!error} className={className}>
      {!questionIsOptional && <RequiredMarker>* </RequiredMarker>}
      {label && (
        <label htmlFor={id} className="label" {...labelProps}>
          <span className={"label-text"}>{label}</span> {toggleTip}
        </label>
      )}

      {description && (
        <StyledDescription className="input-description">{description}</StyledDescription>
      )}
      <div className="inputWrapper">
        <input
          id={id}
          className="input"
          type={type}
          ref={ref}
          {...inputProps}
          aria-required={!questionIsOptional}
        />
        {adornmentIcon && (
          <FontAwesomeIcon className="adornmentIcon" icon={adornmentIcon} size="1x" />
        )}
      </div>
      {error && (
        // eslint-disable-next-line
        // @ts-ignore */
        <span className="errorText" data-test={`${inputProps["data-test"]}-error`}>
          {error}
        </span>
      )}
      {helperText && !error && <span className="helperText">{helperText}</span>}
    </StyledInput>
  );
});

InputComponent.displayName = "Input";

export default InputComponent;

function BaseDateInput({
  label,
  helperText,
  className,
  error,
  inputProps,
  toggleTip,
  ...dayPickerProps
}: {
  toggleTip?: ReactElement<ToggleTipProps>;
  label?: string;
  helperText?: string;
  className?: string;
  error?: string;
} & DayPickerInputProps) {
  return (
    <StyledInput hasError={!!error} className={className}>
      {label && <label className="label">{label}</label>} {toggleTip}
      <div className="inputWrapper">
        <DayPickerInput
          placeholder="YYYY-MM-DD"
          inputProps={{ ...inputProps, className: "input" }}
          {...dayPickerProps}
        />
        <FontAwesomeIcon className="adornmentIcon" icon="calendar-day" size="1x" />
      </div>
      {error && <span className="errorText">{error}</span>}
      {helperText && !error && <span className="helperText">{helperText}</span>}
    </StyledInput>
  );
}

export const DateInput = wrapDayPickerStyles(BaseDateInput);

export function PhoneInput({
  label,
  helperText,
  value,
  className,
  error,
  placeHolder = "(555) 555-5555",
  onChange,
  onCountryChange,
}: {
  value: string;
  onChange: (newValue: string | undefined) => void;
  onCountryChange: (countryCode?: string) => void;
  placeHolder?: string;
  label?: string;
  helperText?: string;
  className?: string;
  error?: string;
}) {
  return (
    <StyledInput hasError={!!error} className={className}>
      {label && <label className="label">{label}</label>}
      <div className="inputWrapper">
        <CustomPhoneInput
          defaultCountry="US"
          displayInitialValueAsLocalNumber
          className="input phone"
          placeholder={placeHolder}
          value={value}
          onChange={onChange}
          onCountryChange={onCountryChange}
        />
        <FontAwesomeIcon className="adornmentIcon" icon="phone" size="1x" />
      </div>
      {error && <span className="errorText">{error}</span>}
      {helperText && !error && <span className="helperText">{helperText}</span>}
    </StyledInput>
  );
}
